import { type ReactNode, type RefObject, useEffect, useState } from 'react'

import { QuoteCard } from '@/components/quotes/QuoteCard'
import { QuoteCardMini } from '@/components/quotes/QuoteCardMini'
import type { PricedQuoteCard } from '@/components/quotes/quoteCardTypes'
import { cn } from '@/lib/utils'
import { PacedFocusReveal } from './PacedFocusReveal'
import { prefersReducedMotion } from './useQuoteAnimation'

/**
 * Both panels are this wide in every scene. A panel that resized when the second card
 * arrived would re-wrap the first card's price, coverage rows and carrier detail — so
 * the anchor holds its width and only slides.
 */
const PANEL_WIDTH = 448
const PANEL_GAP = 16
const RAIL_WIDTH = 224
const RAIL_GAP = 20

const PANELS_WIDTH = PANEL_WIDTH * 2 + PANEL_GAP
const TOTAL_WIDTH = PANELS_WIDTH + RAIL_GAP + RAIL_WIDTH
/** How far the pair travels: alone, the anchor sits in the second slot, flush against the rail. */
const PANEL_STEP = PANEL_WIDTH + PANEL_GAP
/** How far across a panel the eye settles, biased toward the edge facing the other card. */
const READ_BIAS = 0.85
/** Where the pointer reads on each panel, in the row's own coordinates. Biased inward so
 *  comparing the two is a short glance across, not a swing between their far centres. */
export const PANEL_READ_POINTS = [
  PANEL_WIDTH * READ_BIAS,
  PANEL_STEP + PANEL_WIDTH * (1 - READ_BIAS),
] as const
/** Pulls the one-card group back to the middle, since it is a panel narrower than the pair. */
const RECENTRE = PANEL_STEP / 2

/**
 * Height the card gains once it is in the cart: it swaps one h-10 CTA for an h-10 "Added to
 * cart" plus a gap-1 and an h-8 Remove. Held as padding until then, so the click changes no
 * height — otherwise the card jumps and shoves the bottom-aligned rail down with it.
 */
const CART_RESERVE = 36

/** Marketing runs the card's opening faster than the product does — this is a loop, not a page load. */
const CARD_PACE = 0.55
/** Clears the card's three beats at CARD_PACE, so the rail follows rather than racing it. */
const RAIL_DELAY_MS = 800
/** Gap between each rail item's entrance. */
const RAIL_STAGGER_MS = 60

const noop = () => undefined

interface ComparisonViewerProps {
  quotes: PricedQuoteCard[]
  anchor: PricedQuoteCard
  compared: PricedQuoteCard
  /** The second panel is open, so the pair slides left into its side-by-side position. */
  showsSecondCard: boolean
  /** Coverage keys the anchor card should render as already in the cart. */
  cartKeys: ReadonlySet<string>
  /** Measured for the cursor, which lands on this column's Add to cart. */
  anchorRef: RefObject<HTMLDivElement | null>
  /** Rendered inside the anchor column, so it is positioned against the real button. */
  cursor?: ReactNode
}

/**
 * The focused viewer as the product renders it: up to two cards side by side with a
 * rail of every other quote, each card opening in the same three beats.
 */
export function ComparisonViewer({
  quotes,
  anchor,
  compared,
  showsSecondCard,
  cartKeys,
  anchorRef,
  cursor,
}: ComparisonViewerProps) {
  const railQuotes = quotes.filter((quote) => quote.id !== anchor.id)
  const inCart = cartKeys.size > 0
  // Reduced motion starts on the final frame; killing the transition alone would still pop.
  const [railIn, setRailIn] = useState(prefersReducedMotion)

  useEffect(() => {
    if (railIn) return
    const timer = window.setTimeout(() => setRailIn(true), RAIL_DELAY_MS)
    return () => clearTimeout(timer)
  }, [railIn])

  // Opacity and translate only: the rail holds its space so the bottom alignment never shifts.
  const railItem = (index: number) => ({
    className: cn(
      'transition-[opacity,translate] duration-300 ease-out motion-reduce:transition-none',
      railIn ? 'translate-x-0 opacity-100' : 'translate-x-2 opacity-0'
    ),
    style: { transitionDelay: `${index * RAIL_STAGGER_MS}ms` },
  })

  return (
    <div
      className="relative mx-auto flex items-stretch transition-transform duration-500 ease-out"
      style={{
        width: TOTAL_WIDTH,
        gap: RAIL_GAP,
        transform: `translateX(${showsSecondCard ? 0 : -RECENTRE}px)`,
      }}
    >
      {/* Clipped, so the arriving card is revealed at the edge rather than crossing the rail. */}
      <div className="shrink-0 overflow-hidden" style={{ width: PANELS_WIDTH }}>
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{
            gap: PANEL_GAP,
            transform: `translateX(${showsSecondCard ? 0 : PANEL_STEP}px)`,
          }}
        >
          <div
            ref={anchorRef}
            className="relative shrink-0"
            style={{ width: PANEL_WIDTH, paddingBottom: inCart ? 0 : CART_RESERVE }}
          >
            <PacedFocusReveal pace={CARD_PACE}>
              <QuoteCard quote={anchor} isFocused cartKeys={cartKeys} />
            </PacedFocusReveal>
          </div>
          {showsSecondCard && (
            <div className="animate-in fade-in shrink-0 duration-500" style={{ width: PANEL_WIDTH }}>
              <PacedFocusReveal pace={CARD_PACE}>
                <QuoteCard quote={compared} isFocused />
              </PacedFocusReveal>
            </div>
          )}
        </div>
      </div>
      {/* Bottom-aligned with the cards, the way it sits in the real viewer. */}
      {/* Outside the panel track: the pointer is the viewer's hand, so the clip and the
          pair's slide must not carry it. The track sits at this element's origin, so the
          row's coordinates are these coordinates. */}
      {cursor}
      <aside className="flex shrink-0 flex-col justify-end gap-2" style={{ width: RAIL_WIDTH }}>
        <span {...railItem(0)}>
          <span className="text-ink-200 block px-1 text-xs font-bold tracking-wide uppercase">
            Compare with
          </span>
        </span>
        {railQuotes.map((quote, index) => (
          <div key={quote.id} {...railItem(index + 1)}>
            <QuoteCardMini
              quote={quote}
              active={showsSecondCard && quote.id === compared.id}
              onClick={noop}
            />
          </div>
        ))}
      </aside>
    </div>
  )
}
